import _ from "lodash";
import React, { ReactNode } from "react";
import {
  FACEBOOK_COLOR,
  GOOGLE_COLOR,
  INSTAGRAM_COLOR,
  LINKED_IN_BUSINESS_COLOR,
  MERCADOLIBRE_COLOR,
  WOO_COMMERCE_COLOR,
} from "../../../constants/socialColors.constant";
import { SocialSettings } from "../../../cores/me/interfaces/api/get-meSocialSettings";
import { SocialProviderId, SocialProviderNameMapper } from "../../../cores/socialPost/config";
import { SocialProvider } from "../../../cores/socialPost/interfaces/socialProvider";
import {
  hasAnySummaryPlatform,
  SummaryPlatformSources,
} from "../analytics/summary/summarySyncedPlatforms";
import { TIKTOK_BLACK } from "../analytics/tiktokBrandColors";
import { FacebookIcon, IconProps } from "../social/Facebook/FacebookIcon";
import { GooglenAnalyticsIcon } from "../social/GoogleAnalitycs/GoogleAnalitycsIcon";
import { InstagramIcon } from "../social/Instagram/InstagramIcon";
import { MercadolibreIcon } from "../social/Mercadolibre/MercadolibreIcon";
import { TiktokIcon } from "../social/Tiktok/TiktokIcon";
import { LinkedInBusinessIcon } from "../social/LinkedIn/LinkedInBusinessIcon";
import { WooCommerceIcon } from "../social/WooCommerce/WooCommerceIcon";

export const SUMMARY_REPORT_ID = "Summary";

const ICON_BY_KEY: Record<string, (props: IconProps) => ReactNode> = {
  facebook: (props) => React.createElement(FacebookIcon, props),
  instagram: (props) => React.createElement(InstagramIcon, props),
  google: (props) => React.createElement(GooglenAnalyticsIcon, props),
  wooCommerce: (props) => React.createElement(WooCommerceIcon, props),
  mercadoLibre: (props) => React.createElement(MercadolibreIcon, props),
  tiktok: (props) => React.createElement(TiktokIcon, props),
  linkedInBusiness: (props) => React.createElement(LinkedInBusinessIcon, props),
};

const SETTINGS_KEY_BY_PROVIDER: Record<string, keyof SocialSettings | "tiktok" | "linkedInBusiness"> = {
  [SocialProviderId.Facebook]: "facebook",
  [SocialProviderId.Instagram]: "instagram",
  [SocialProviderId.Google]: "google",
  [SocialProviderId.WooCommerce]: "wooCommerce",
  [SocialProviderId.Mercadolibre]: "mercadoLibre",
  [SocialProviderId.TikTok]: "tiktok",
  [SocialProviderId.LinkedInBusiness]: "linkedInBusiness",
};

export const INDIVIDUAL_REPORT_PROVIDERS = [
  { id: SocialProviderId.Facebook, color: FACEBOOK_COLOR },
  { id: SocialProviderId.Instagram, color: INSTAGRAM_COLOR },
  { id: SocialProviderId.TikTok, color: TIKTOK_BLACK },
  { id: SocialProviderId.LinkedInBusiness, color: LINKED_IN_BUSINESS_COLOR },
  { id: SocialProviderId.Google, color: GOOGLE_COLOR },
  { id: SocialProviderId.WooCommerce, color: WOO_COMMERCE_COLOR },
  { id: SocialProviderId.Mercadolibre, color: MERCADOLIBRE_COLOR },
];

export const isReportProviderSynced = (
  providerId: string,
  sources: SummaryPlatformSources
): boolean => {
  const settingsKey = SETTINGS_KEY_BY_PROVIDER[providerId];
  if (!settingsKey) return false;
  const platforms = {
    facebook:
      Boolean(sources.socialSettings?.facebook) ||
      !_.isEmpty(sources.facebookSettings),
    instagram:
      Boolean(sources.socialSettings?.instagram) ||
      !_.isEmpty(sources.instagramSettings),
    tiktok: !_.isEmpty(sources.tiktokSettings),
    linkedinBusiness:
      Boolean(sources.socialSettings?.linkedInBusiness) ||
      !_.isEmpty(sources.linkedInBusinessSettings),
  };
  if (settingsKey === "tiktok") return platforms.tiktok;
  if (settingsKey === "linkedInBusiness") return platforms.linkedinBusiness;
  if (settingsKey === "facebook") return platforms.facebook;
  if (settingsKey === "instagram") return platforms.instagram;
  return !_.isEmpty(sources.socialSettings?.[settingsKey]);
};

export const canShowSummaryReport = (sources: SummaryPlatformSources) =>
  hasAnySummaryPlatform(sources);

export const buildIndividualReportProviders = (
  sources: SummaryPlatformSources
): SocialProvider[] =>
  INDIVIDUAL_REPORT_PROVIDERS.filter((provider) =>
    isReportProviderSynced(provider.id, sources)
  ).map((provider) => {
    const settingsKey = SETTINGS_KEY_BY_PROVIDER[provider.id] ?? provider.id;
    const labelKey =
      settingsKey === "mercadoLibre" ? "mercadolibre" : settingsKey;
    return {
      id: provider.id,
      color: provider.color,
      name: SocialProviderNameMapper[labelKey] ?? provider.id,
      icon: ICON_BY_KEY[settingsKey],
    };
  });
